import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { BehaviorSubject, of, lastValueFrom } from 'rxjs';
import { AuthService } from 'providers/auth.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { EnvironmentService } from 'providers/environment.service';
import { DatePipe } from '@angular/common';

export interface ReleaseNote {
    titleOriginal: string;
    title: string;
    noteId: string;
    releaseDate: string;
    versionId: string | undefined;
    htmlString: string;
    isDraft: boolean;
    isInternal: boolean;
    routerLink: string;
}
@Injectable({
    providedIn: 'root',
})
@UntilDestroy()
export class ReleaseNotesService {
    private readonly releaseType$ = new BehaviorSubject<'chargeye' | 'firmware'>('chargeye');
    // Local storage key to store seen versions of release notes
    private readonly STORAGE_KEY = 'release_notes_viewed';
    // State to display notification or not
    private readonly _notification: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    notification$ = this._notification.asObservable();

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private environmentService: EnvironmentService
    ) {
        this.environmentService.getAppOrigin$().subscribe((origin) => {
            this.releaseType$.next(origin === 'mykempower' ? 'firmware' : 'chargeye');
        });
    }

    checkReleaseNoteExists(type: 'chargeye' | 'firmware', id: string) {
        return this.http.get(`/assets/release-notes/${type}/${id}.html`, { responseType: 'text' }).pipe(
            map(() => true),
            catchError(() => of(false))
        );
    }

    // Custom comparison function for Dates
    compareDates = (date1: string, date2: string) => date2.localeCompare(date1);

    // Update release notes notification
    updateNotification(newStatus: boolean) {
        this._notification.next(newStatus);
    }

    // Get seen release note versions from local storage
    getSeenVersionsFromLocal(): string[] {
        const versions = localStorage.getItem(this.STORAGE_KEY);
        return versions ? JSON.parse(versions) : [];
    }
    parseReleaseNotes(htmlString: string, opts?: { isDraft: boolean }): ReleaseNote[] {
        return htmlString
            ?.split('<h2>')
            ?.slice(1)
            ?.map((noteRaw) => {
                const titleRaw = noteRaw.split('</h2>')[0];
                const isDraft = titleRaw.includes('(DRAFT)') || opts?.isDraft || false;
                const title = titleRaw.replace('(DRAFT)', '').trim();
                const body = noteRaw.split('</h2>')[1];
                // match version number and date e.g. 3.5.0 (2025-03-24)
                const versionId = title.match(/^\d+\.\d+\.\d+/)?.[0] || title.split(' ')[1];
                let releaseDate = title.match(/\((\d{4}-\d{2}-\d{2})\)/)?.[1] || title.split(' ')[0];

                // check that the release date is in the format YYYY-MM-DD
                const isValidDate = /^\d{4}-\d{2}-\d{2}$/.test(releaseDate);
                if (!isValidDate) {
                    releaseDate = undefined;
                }
                const isInternal = body.includes('#INTERNAL');
                const noteId = this.constructNoteId(releaseDate, versionId, isDraft);
                const datePipe = new DatePipe('en-US');
                const formattedDate = releaseDate ? datePipe.transform(releaseDate, 'dd.MM.yyyy') : '';

                const note = {
                    title: versionId ? `Firmware v${versionId}` : formattedDate,
                    titleOriginal: title,
                    noteId,
                    releaseDate,
                    versionId,
                    htmlString: body.trim(),
                    isDraft,
                    isInternal,
                    routerLink: versionId ?? `${noteId}`,
                };
                return note;
            });
    }
    constructNoteId(releaseDate: string, versionId: string | undefined, isDraft: boolean) {
        let id = `${releaseDate}`;
        if (versionId) {
            id = `${versionId}_${releaseDate}`;
        }
        if (isDraft) {
            id += '-draft';
        }
        return id;
    }

    async getMainReleaseNotesHtml(
        getDocument: boolean,
        releaseType?: 'chargeye' | 'firmware'
    ): Promise<string | Document> {
        await this.authService.userIsAuthed();
        const htmlString = await this.http
            .get(`/assets/release-notes/${releaseType || this.releaseType$.value}/main.html`, {
                responseType: 'text',
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=300000', // 5 minutes
                },
            })
            .pipe(map((val) => val))
            .toPromise();
        if (getDocument) {
            const parser = new DOMParser();
            return parser.parseFromString(htmlString, 'text/html');
        }
        return htmlString;
    }

    async getFirmwareReleaseNoteHtml(id: string): Promise<string> {
        await this.authService.userIsAuthed();
        const htmlString = await lastValueFrom(
            this.http.get(`/assets/release-notes/firmware/${id}.html`, {
                responseType: 'text',
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=300000',
                },
            })
        );
        return htmlString;
    }
}
